import React, { useState, useEffect } from "react";
import {
  User,
  ShoppingCart,
  PhoneCall,
  ChevronRight,
  LogOut,
  Package,
  User2,
  ChevronDown,
  ChevronUp,
  Menu,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCategories } from "../../hooks/useCategories";
import { useDepartaments } from "../../hooks/useDepartaments";
import { Link } from "react-router-dom";
import { useCartCount } from "../../hooks/useCartCount";

const MobileMenu = ({ setMobileMenuOpen, isMenuOpen }) => {
  const [openCategory, setOpenCategory] = useState(null);
  const [showDepartments, setShowDepartments] = useState(false);
  const [cartAnimation, setCartAnimation] = useState(false);
  const [showCartIcon, setShowCartIcon] = useState(false);
  const { count: cartCount } = useCartCount();

  // Animação do carrinho e troca de ícone
  useEffect(() => {
    if (cartCount > 0) {
      setCartAnimation(true);
      setShowCartIcon(true);

      const animationTimer = setTimeout(() => setCartAnimation(false), 1000);
      const iconTimer = setTimeout(() => setShowCartIcon(false), 3000);

      return () => {
        clearTimeout(animationTimer);
        clearTimeout(iconTimer);
      };
    }
  }, [cartCount]);

  const closeMenu = () => {
    setMobileMenuOpen(false);
  };

  const toggleCategory = (categoryId) => {
    setOpenCategory(openCategory === categoryId ? null : categoryId);
  };

  const toggleDepartments = () => {
    setShowDepartments(!showDepartments);
  };

  const {
    data: categories,
    isLoading: isLoadingCategories,
    error: categoriesError,
  } = useCategories();
  const {
    data: departments,
    isLoading: isLoadingDepartments,
    error: departmentsError,
  } = useDepartaments();

  const departmentsByCategory = departments?.reduce((acc, department) => {
    if (!acc[department.category_id]) {
      acc[department.category_id] = [];
    }
    acc[department.category_id].push(department);
    return acc;
  }, {});

  const createSafeUrl = (url) => {
    if (!url) return "";
    return url.replace(/\//g, "");
  };
  return (
    <div className="fixed top-0 left-0 right-0 bg-gray-900 shadow-xl md:hidden z-40">
      <div className="sticky top-0 bg-gray-900 p-4 flex justify-between items-center z-10 border-b border-gray-800">
        <motion.button
          onClick={() => setMobileMenuOpen(!isMenuOpen)}
          className="text-white focus:outline-none relative"
          whileTap={{ scale: 0.9 }}>
          {showCartIcon ? (
            <motion.div
              key="cart"
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 500, damping: 20 }}
              className="relative">
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring" }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cartCount}
                </motion.span>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}>
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </motion.div>
          )}
        </motion.button>
      </div>
      <div className="p-4 border-t border-gray-800">
        <div className="flex flex-col gap-4">
          {/* Action Buttons */}
          <div className="flex justify-around py-3 border-b border-gray-800">
            <Link
              to="https://wa.me/5561984497981"
              target="_blank"
              className="flex flex-col items-center text-white no-underline relative"
              onClick={closeMenu}>
              <PhoneCall className="w-5 h-5" />
              <span className="text-xs mt-1">Atendimento</span>
            </Link>
            <Link
              to="/minha-conta"
              className="flex flex-col items-center text-white no-underline relative"
              onClick={closeMenu}>
              <User className="w-5 h-5" />
              <span className="text-xs mt-1">Minha Conta</span>
            </Link>
            <Link
              to="/cart"
              className="flex flex-col items-center bg-transparent border-none text-white transition-colors hover:text-green-400 no-underline relative">
              <motion.div
                animate={
                  cartAnimation
                    ? {
                        y: [0, -5, 0],
                        rotate: [0, 10, -10, 0],
                        scale: [1, 1.2, 1],
                      }
                    : {}
                }
                transition={{ duration: 0.6 }}>
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <motion.span
                    key={cartCount}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring" }}
                    className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full px-2">
                    {cartCount}
                  </motion.span>
                )}
              </motion.div>
              <span className="text-xs mt-1">Carrinho</span>
            </Link>
          </div>

          {/* Departments/Categories */}
          <div className="mt-2">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-white text-lg">DEPARTAMENTOS</h3>
              <button onClick={toggleDepartments} className="text-white p-1">
                {showDepartments ? <ChevronUp /> : <ChevronDown />}
              </button>
            </div>

            {showDepartments && (
              <div className="flex flex-col gap-1">
                {isLoadingCategories || isLoadingDepartments ? (
                  <div className="p-2 text-white">Carregando...</div>
                ) : categoriesError || departmentsError ? (
                  <div className="p-2 text-white">
                    Erro ao carregar departamentos
                  </div>
                ) : (
                  categories &&
                  categories.map((category) => {
                    const categoryDepartments =
                      departmentsByCategory?.[category.id] || [];
                    return (
                      <div key={category.id}>
                        <div className="flex justify-between items-center">
                          <Link
                            to={`/categoria/${createSafeUrl(category.url)}`}
                            className="flex justify-between items-center p-2 text-white no-underline rounded w-full hover:bg-gray-800"
                            onClick={closeMenu}>
                            <span>{category.name}</span>
                          </Link>
                          {categoryDepartments.length > 0 && (
                            <button
                              onClick={() => toggleCategory(category.id)}
                              className="p-2 text-gray-400">
                              {openCategory === category.id ? (
                                <ChevronUp size={16} />
                              ) : (
                                <ChevronDown size={16} />
                              )}
                            </button>
                          )}
                        </div>
                        {categoryDepartments.length > 0 &&
                          openCategory === category.id && (
                            <div className="flex flex-col gap-1 ml-4">
                              {categoryDepartments.map((department) => (
                                <Link
                                  key={department.id}
                                  to={`/departamento/${department.name}`}
                                  className="p-2 text-white no-underline rounded hover:bg-gray-800"
                                  onClick={closeMenu}>
                                  {department.name}
                                </Link>
                              ))}
                            </div>
                          )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* User Profile Items */}
          <div className="mt-4 pt-4 border-t border-gray-800">
            <h3 className="font-bold text-white text-lg mb-2">MINHA CONTA</h3>
            <div className="flex flex-col gap-1">
              <Link
                to="/minha-conta"
                className="flex items-center p-2 text-white no-underline rounded hover:bg-gray-800"
                onClick={closeMenu}>
                <User2 className="w-4 h-4 mr-2" />
                Minha Conta
              </Link>
              <Link
                to="/minha-conta?tab=meus-pedidos"
                className="flex items-center p-2 text-white no-underline rounded hover:bg-gray-800"
                onClick={closeMenu}>
                <Package className="w-4 h-4 mr-2" />
                Meus Pedidos
              </Link>
              <Link
                to="/logout"
                className="flex items-center p-2 text-white no-underline rounded hover:bg-gray-800"
                onClick={closeMenu}>
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Link>
            </div>
          </div>

          <button
            onClick={() => setMobileMenuOpen(false)}
            className="mt-4 bg-orange-500 text-white py-2 px-4 rounded w-full hover:bg-orange-600 transition-colors duration-200">
            Fechar Menu
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
